import React, { useState, useEffect, ReactNode } from 'react'
import { Typography } from '../..'
import { IconArrowDown } from '@s4l/icons/IconArrowDown'
import { IconArrowUp } from '@s4l/icons/IconArrowUp'
import { cn, UtilityClassNames } from '@s4l/utility-css'
import debug from 'debug'
import styles from './styles.module.css'

const log = debug('s4l:ds:Collapsible')

export interface CollapsibleProps {
  title: string
  children: ReactNode
  className?: UtilityClassNames
  open?: boolean
  onToggle?: (isOpen: boolean) => void
  defaultOpen?: boolean
  state?: 'default' | 'hover' | 'active' | 'focus'
}

export default function Collapsible(props: Readonly<CollapsibleProps>) {
  const { title, children, className = '', open, onToggle, defaultOpen = false, state } = props
  const isControlled = open !== undefined
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isOpen = isControlled ? open! : internalOpen

  const id = title.replace(/[^\w-]+/g, '-').toLowerCase()
  const buttonId = `collapsible-button-${id}`
  const contentId = `collapsible-content-${id}`

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (open !== undefined && defaultOpen !== undefined) {
        log(
          'Collapsible should not have both "open" and "defaultOpen" props. Use "open" for controlled mode or "defaultOpen" for uncontrolled mode'
        )
      }
    }
  }, [open, defaultOpen])

  const toggleOpen = () => {
    const newOpenState = !isOpen
    if (isControlled) {
      onToggle?.(newOpenState)
    } else {
      setInternalOpen(newOpenState)
    }
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      <button
        className={styles.header}
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-controls={contentId}
        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${title}`}
        id={buttonId}
        data-state={state}
        type="button"
      >
        <Typography variant="title" size="xs" ellipsis>
          {title}
        </Typography>
        <div>{isOpen ? <IconArrowUp /> : <IconArrowDown />}</div>
      </button>

      <div
        className={cn(styles.contentWrapper, isOpen ? styles.open : '')}
        id={contentId}
        aria-hidden={!isOpen}
        role="region"
        aria-labelledby={buttonId}
      >
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  )
}
